import { memo, useEffect, useMemo } from 'react'
import { useGlobalState } from '@highlight/ipc/react'
import { LoadingSpinner } from '@renderer/chat'
import { DownloadState } from '@shared/ipc/GlobalStateUtils'
import { Toast, useToast } from '@renderer/overlay/Toaster'

type AsrSetupToastProps = {
  toastId: string
}

export const AsrSetupToast = memo(({ toastId }: AsrSetupToastProps) => {
  const toast = useToast()
  const [isInstallingASR] = useGlobalState<boolean>('asr.isInstalling')
  const [yamnetDownloadState] = useGlobalState<DownloadState>('yamnet.download')
  const [whisperDownloadState] = useGlobalState<DownloadState>('whisper.download')

  const state = useMemo(() => {
    if (yamnetDownloadState?.downloaded && whisperDownloadState?.downloaded) {
      return {
        title: 'Audio Setup Complete',
        description: 'You can now use Highlight to transcribe audio.',
      }
    } else if (yamnetDownloadState?.status === 'downloading') {
      return {
        title: 'Downloading Audio Labeling Model',
        description: 'Hang tight! We\u2019ll start your audio note as soon as the model is ready.',
        progress: yamnetDownloadState.percent,
      }
    } else if (whisperDownloadState?.status === 'downloading') {
      return {
        title: 'Downloading transcription model',
        description: 'Hang tight! We\u2019ll start your audio note as soon as the model is ready',
        progress: whisperDownloadState.percent,
      }
    } else if (isInstallingASR) {
      return {
        title: 'Getting things ready\u2026',
        description: 'Please wait while we get you set up to take audio notes',
      }
    } else {
      return {
        title: 'Audio Setup Complete',
        description: 'You can now use Highlight to transcribe audio.',
      }
    }
  }, [isInstallingASR, yamnetDownloadState, whisperDownloadState])

  const updateState = useMemo(() => {
    if (yamnetDownloadState?.status === 'downloading' || whisperDownloadState?.status === 'downloading') {
      return 'downloading'
    }

    if (yamnetDownloadState?.downloaded && whisperDownloadState?.downloaded) {
      return 'ready'
    }

    return 'error'
  }, [isInstallingASR, yamnetDownloadState, whisperDownloadState])

  useEffect(() => {
    if (updateState === 'ready') {
      const timer = setTimeout(() => toast.dismiss(toastId), 5000)
      return () => clearTimeout(timer)
    }
  }, [updateState, toast, toastId])

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <Toast.Description>{state.description}</Toast.Description>
        {updateState === 'downloading' && state.progress != null && (
          <Toast.Footer>
            <span className="flex items-center gap-2">
              <LoadingSpinner />
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{state.progress.toFixed(0)}%</span>
            </span>
          </Toast.Footer>
        )}
      </Toast.Content>
    </Toast>
  )
})

AsrSetupToast.displayName = 'AsrSetupToast'
